import type { UserListItem } from '@planuze/admin-contracts';
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@planuze/ui';
import { useState } from 'react';
import { useUsersContent } from '../../content/sections/users.js';
import { useDebounce } from '../../hooks/use-debounce.js';
import { useUsersList } from '../../hooks/use-users.js';

interface Props {
  readonly value: UserListItem | null;
  readonly onChange: (user: UserListItem) => void;
  /** Esconde este id da lista (ex.: o próprio usuário de origem). */
  readonly excludeId?: string;
  /** Texto do trigger quando nada selecionado (default: destino de transferência, via catálogo). */
  readonly placeholder?: string;
}

/* Autocomplete de usuário (busca por email). Usado p/ destino de transferência e p/
   owner inicial de org (admin-created, ADR-0421). Backend valida em qualquer caso. */
export function UserPicker({ value, onChange, excludeId, placeholder }: Props) {
  const copy = useUsersContent();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(query, 250);
  const { data } = useUsersList({ search: debounced.length >= 2 ? debounced : undefined });
  const items = (data?.pages.flatMap((page) => page.items) ?? []).filter((u) => u.id !== excludeId);
  const resolvedPlaceholder = placeholder ?? copy.picker.defaultPlaceholder;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" className="w-full justify-start">
          {value !== null ? value.email : resolvedPlaceholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2">
        <Input
          value={query}
          placeholder={copy.picker.searchPlaceholder}
          aria-label={copy.picker.searchAriaLabel}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ul className="mt-2 max-h-60 overflow-auto">
          {items.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                className="w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/15"
                onClick={() => {
                  onChange(user);
                  setOpen(false);
                }}
              >
                {user.email}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
